import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Slider, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { useGmSession } from '../useGmSession';
import TwoJointPoleScene from '../visuals/TwoJointPoleScene';
import { TjpSwitchableScene } from '../visuals/TjpSceneView';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import TwoJointPoleMultiplayer from './TwoJointPoleMultiplayer';
import TwoJointPoleModePicker from './TwoJointPoleModePicker';
import TwoJointPoleOnline, { roomIdFromLocation } from './TwoJointPoleOnline';
import {
    createTjpSoloSession,
    deleteTjpSoloSession,
    moveTjpSoloSession,
    DT,
} from './tjpGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { COLOR } from '../ui/tokens';
import { gmStartErrorMessage } from '../gmLease';
import { GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'two_joint_pole';
const { UPLOAD_URL } = ARCADE.two_joint_pole;
const ACCENT = GAME_ACCENT.two_joint_pole;
const CART_MASS = 1.0;
const POLE1_LENGTH = 0.5;
const POLE2_LENGTH = 0.75;
const CART_FRICTION = 0.0005;
const POLE_DAMPING = 0.02;
const COUPLING_FACTOR = 0.05;
const MAX_STEPS = 2000;
const PIVOT_HEIGHT = 0.0;
const GROUND_EPS = 1e-3;
export const DEFAULT_STIFFNESS = 0.8;
export const DEFAULT_GRAVITY = 0.18;
export const DEFAULT_POLE_MASS = 0.12;

export const PHYSICS_PRESETS = {
    easy: {
        label: 'Easy',
        hint: 'Stiff joints, light poles, low gravity',
        stiffness: DEFAULT_STIFFNESS,
        gravity: DEFAULT_GRAVITY,
        poleMass: DEFAULT_POLE_MASS,
    },
    balanced: {
        label: 'Balanced',
        hint: 'Moderate restore, Earth-ish fall',
        stiffness: 0.55,
        gravity: 0.42,
        poleMass: 0.38,
    },
    hard: {
        label: 'Hard',
        hint: 'Floppy joints, heavy poles, snappy fall',
        stiffness: 0.2,
        gravity: 0.78,
        poleMass: 0.72,
    },
};

const HINT = '← → / A D apply force · Esc pause';

export const clamp01 = (value, fallback = 0) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(0, Math.min(1, n));
};
export const clampStiffness = (value) => clamp01(value, DEFAULT_STIFFNESS);

export const jointGains = (stiffness = DEFAULT_STIFFNESS) => {
    const s = clampStiffness(stiffness);
    return {
        restore: s * 7.2,
        align: COUPLING_FACTOR + s * 3.2,
        damp: POLE_DAMPING + s * 0.28,
        noise: 0.004 * (1 - 0.85 * s),
    };
};

export const gravityMs2 = (gravity = DEFAULT_GRAVITY) => 0.9 + clamp01(gravity, DEFAULT_GRAVITY) * 18.7;

export const poleMassKg = (poleMass = DEFAULT_POLE_MASS) => 0.018 + clamp01(poleMass, DEFAULT_POLE_MASS) * 0.22;

const initialPhysics = () => ({
    cart_position: 0,
    cart_velocity: 0,
    pole1_angle: (Math.random() - 0.5) * 0.1,
    pole1_velocity: 0,
    pole2_angle: (Math.random() - 0.5) * 0.16,
    pole2_velocity: 0,
});

/** Kept for unit tests — runtime 1P play uses the GM. */
export const computeAcc = (physics, force, stiffness = DEFAULT_STIFFNESS, env = {}) => {
    const { restore, align, damp } = jointGains(stiffness);
    const g = gravityMs2(env.gravity);
    const mPole = poleMassKg(env.poleMass);
    const totalMass = CART_MASS + mPole * 2;
    const cartAcc = (force - CART_FRICTION * physics.cart_velocity) / totalMass;
    const theta1 = physics.pole1_angle;
    const cos1 = Math.cos(theta1);
    const theta1Acc = (
        g * Math.sin(theta1)
        - restore * theta1
        - cartAcc * cos1
        - damp * physics.pole1_velocity
        + align * (physics.pole2_angle - theta1)
    ) / (POLE1_LENGTH * (4 / 3 - (mPole * cos1 * cos1) / totalMass));
    const theta2 = physics.pole2_angle;
    const cos2 = Math.cos(theta2);
    const theta2Acc = (
        g * Math.sin(theta2)
        - restore * theta2
        - cartAcc * cos2 * 0.8
        - damp * physics.pole2_velocity
        + align * (theta1 - theta2)
    ) / (POLE2_LENGTH * (4 / 3 - (mPole * cos2 * cos2) / totalMass));
    return { cartAcc, theta1Acc, theta2Acc };
};

export const poleEndpointHeights = (physics) => {
    const yJoint = PIVOT_HEIGHT + POLE2_LENGTH * Math.cos(physics.pole2_angle);
    const yTip = yJoint + POLE1_LENGTH * Math.cos(physics.pole1_angle);
    return { yJoint, yTip };
};

export const checkDone = (physics, stepCount) => {
    const { yJoint, yTip } = poleEndpointHeights(physics);
    if (yJoint <= GROUND_EPS || yTip <= GROUND_EPS) return 'pole touched the ground';
    if (stepCount >= MAX_STEPS) return 'maximum steps reached';
    return null;
};

const TwoJointPoleSinglePlayer = ({ gameMode, setGameMode }) => {
    const play = useManualPlay();
    const [physics, setPhysics] = useState(initialPhysics);
    const [stepCount, setStepCount] = useState(0);
    const [totalReward, setTotalReward] = useState(0);
    const [isGameOver, setIsGameOver] = useState(false);
    const [message, setMessage] = useTransientMessage();
    const [stiffness, setStiffness] = useState(DEFAULT_STIFFNESS);
    const [gravity, setGravity] = useState(DEFAULT_GRAVITY);
    const [poleMass, setPoleMass] = useState(DEFAULT_POLE_MASS);
    const metaRef = useRef({ stepCount: 0, totalReward: 0, over: false });
    const actionRef = useRef('stay');
    const phaseRef = useRef(play.phase);
    const inFlightRef = useRef(false);
    const [runConfig, setRunConfig] = useState({ stiffness, gravity, poleMass });

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });
    const gm = useGmSession({
        createSession: createTjpSoloSession,
        deleteSession: deleteTjpSoloSession,
    });

    useEffect(() => { phaseRef.current = play.phase; }, [play.phase]);

    const applyGm = useCallback((data) => {
        const phys = data.physics || initialPhysics();
        const steps = typeof data.step_count === 'number' ? data.step_count : 0;
        const reward = typeof data.total_reward === 'number' ? data.total_reward : 0;
        const over = Boolean(data.is_game_over || data.game_over);
        setPhysics(phys);
        setStepCount(steps);
        setTotalReward(reward);
        setIsGameOver(over);
        setMessage(data.message || '');
        metaRef.current = { stepCount: steps, totalReward: reward, over };
        const meta = data.meta || {};
        if (meta.stiffness != null || meta.gravity != null || meta.pole_mass != null) {
            setRunConfig((prev) => ({
                stiffness: meta.stiffness ?? prev.stiffness,
                gravity: meta.gravity ?? prev.gravity,
                poleMass: meta.pole_mass ?? prev.poleMass,
            }));
        }
        return { phys, steps, reward, over, reason: data.terminated_reason };
    }, [setMessage]);

    const startGame = useCallback(async () => {
        const locked = {
            stiffness: clampStiffness(stiffness),
            gravity: clamp01(gravity, DEFAULT_GRAVITY),
            poleMass: clamp01(poleMass, DEFAULT_POLE_MASS),
        };
        setRunConfig(locked);
        try {
            gm.setBusy(true);
            const data = await gm.createFreshSession(locked);
            applyGm(data);
            actionRef.current = 'stay';
            recorder.reset({
                init_state: { physics: { ...(data.physics || {}) } },
                stiffness: locked.stiffness,
                gravity: locked.gravity,
                pole_mass: locked.poleMass,
            });
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            gm.setBusy(false);
        }
    }, [applyGm, gm, gravity, play, poleMass, recorder, stiffness, setMessage]);

    const endGame = useCallback(async () => {
        const last = metaRef.current;
        await gm.clearSession();
        play.endGame({
            score: last.totalReward.toFixed(1),
            scoreLabel: 'Reward',
            details: <Typography variant="body2" sx={{ color: COLOR.textMuted }}>{last.stepCount} steps</Typography>,
        });
        setPhysics(initialPhysics());
        setStepCount(0);
        setTotalReward(0);
        setIsGameOver(false);
        setMessage('');
        metaRef.current = { stepCount: 0, totalReward: 0, over: false };
        actionRef.current = 'stay';
    }, [gm, play, setMessage]);

    const tick = useCallback(async () => {
        if (phaseRef.current !== 'playing') return;
        if (metaRef.current.over) return;
        if (inFlightRef.current) return;
        const sid = gm.sessionIdRef.current;
        if (!sid) return;
        inFlightRef.current = true;
        try {
            const action = actionRef.current || 'stay';
            const data = await moveTjpSoloSession(sid, action);
            const next = applyGm(data);
            recorder.recordTick({
                action,
                reward: data.reward,
                state: { ...next.phys },
            });
            if (next.over) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: next.reward,
                    extra: {
                        total_reward: next.reward,
                        steps: next.steps,
                        terminated_reason: next.reason,
                        stiffness: runConfig.stiffness,
                        gravity: runConfig.gravity,
                        pole_mass: runConfig.poleMass,
                    },
                });
                await gm.clearSession();
            }
        } catch (err) {
            console.warn('TJP solo GM tick failed', err);
        } finally {
            inFlightRef.current = false;
        }
    }, [applyGm, gm, play, recorder, runConfig]);

    useEffect(() => {
        if (play.phase !== 'playing') return undefined;
        const id = setInterval(() => {
            if (phaseRef.current !== 'playing') return;
            tick();
        }, DT * 1000);
        return () => clearInterval(id);
    }, [play.phase, tick]);

    const onKey = useCallback((e) => {
        if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
            actionRef.current = 'left';
            e.preventDefault();
        } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
            actionRef.current = 'right';
            e.preventDefault();
        }
    }, []);

    useEffect(() => {
        const up = (e) => {
            if (['ArrowLeft', 'a', 'A', 'ArrowRight', 'd', 'D'].includes(e.key)) {
                actionRef.current = 'stay';
            }
        };
        window.addEventListener('keyup', up);
        return () => window.removeEventListener('keyup', up);
    }, []);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [],
    });

    return (
        <ArcadeGameShell
            title="Two-Joint"
            titleAccent="Pole"
            subtitle="Choose physics before each episode — keep the double pendulum off the ground"
            accentColor={ACCENT}
            headerContent={(
                <TwoJointPoleModePicker
                    value={gameMode}
                    onChange={setGameMode}
                    accentColor={ACCENT}
                    disabled={play.phase === 'playing' || play.phase === 'paused'}
                />
            )}
            boilerplate={GAME_BOILERPLATE.two_joint_pole}
            session={session}
            formatScore={(e) => {
                const v = e.total_reward ?? e.score ?? e.steps ?? 0;
                return typeof v === 'number' ? v.toFixed?.(1) ?? String(v) : String(v);
            }}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
                minHeight={520}
                showOverlayPlayAgain={false}
                overContent={play.phase === 'over' ? (
                    <Box sx={{ textAlign: 'center' }} data-testid="tjp-final-score">
                        <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                            Reward
                        </Typography>
                        <Typography className="evx-tabular" sx={{ color: ACCENT, fontSize: '2rem', fontWeight: 800, lineHeight: 1.1 }}>
                            {totalReward.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: COLOR.textMuted }}>{stepCount} steps</Typography>
                    </Box>
                ) : null}
                setupContent={(
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                        <PhysicsSetup
                            stiffness={stiffness}
                            gravity={gravity}
                            poleMass={poleMass}
                            onStiffness={setStiffness}
                            onGravity={setGravity}
                            onPoleMass={setPoleMass}
                        />
                    </Box>
                )}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip label={`Steps: ${stepCount}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                            <Chip label={`Reward: ${totalReward.toFixed(1)}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                            <Chip label={isGameOver ? 'Done' : 'Active'} size="small" color={isGameOver ? 'warning' : 'success'} />
                            <Chip label={`Stiffness ${Math.round(runConfig.stiffness * 100)}%`} size="small" variant="outlined" sx={{ color: ACCENT, borderColor: ACCENT }} />
                            <Chip label={`g ${gravityMs2(runConfig.gravity).toFixed(1)}`} size="small" variant="outlined" sx={{ color: ACCENT, borderColor: ACCENT }} />
                            <Chip label={`Poles ${poleMassKg(runConfig.poleMass).toFixed(3)} kg`} size="small" variant="outlined" sx={{ color: ACCENT, borderColor: ACCENT }} />
                        </Stack>
                    </Box>
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Box sx={{ borderRadius: 3, border: '1px solid rgba(142,140,216,0.3)', overflow: 'hidden', bgcolor: '#0c1a24' }}>
                        <TjpSwitchableScene
                            Scene2D={TwoJointPoleScene}
                            cart_position={physics.cart_position}
                            pole1_angle={physics.pole1_angle}
                            pole2_angle={physics.pole2_angle}
                            width={560}
                            height={320}
                        />
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

const TwoJointPole = () => {
    // A shared invite link (?tjp_room=<id>) opens straight into Online 2P.
    const [gameMode, setGameMode] = useState(() => (roomIdFromLocation() ? 'online' : 'single'));
    return (
        <>
            {gameMode === 'single' && <TwoJointPoleSinglePlayer gameMode={gameMode} setGameMode={setGameMode} />}
            {gameMode === 'multi' && <TwoJointPoleMultiplayer gameMode={gameMode} setGameMode={setGameMode} />}
            {gameMode === 'online' && <TwoJointPoleOnline gameMode={gameMode} setGameMode={setGameMode} />}
        </>
    );
};

export default TwoJointPole;

function matchingPreset({ stiffness, gravity, poleMass }) {
    return Object.keys(PHYSICS_PRESETS).find((id) => {
        const p = PHYSICS_PRESETS[id];
        return (
            Math.abs(p.stiffness - stiffness) < 0.02
            && Math.abs(p.gravity - gravity) < 0.02
            && Math.abs(p.poleMass - poleMass) < 0.02
        );
    }) || 'custom';
}

function PhysicsSetup({ stiffness, gravity, poleMass, onStiffness, onGravity, onPoleMass }) {
    const active = matchingPreset({ stiffness, gravity, poleMass });
    const applyPreset = (id) => {
        const p = PHYSICS_PRESETS[id];
        if (!p) return;
        onStiffness(p.stiffness);
        onGravity(p.gravity);
        onPoleMass(p.poleMass);
    };

    return (
        <Box
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
                width: '100%',
                maxWidth: 420,
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(142,140,216,0.35)',
                bgcolor: 'rgba(12, 16, 32, 0.92)',
            }}
        >
            <Typography sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>Episode setup</Typography>
            <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', mb: 1.5 }}>
                Pick a pack or fine-tune. Values lock when the episode starts.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                {Object.entries(PHYSICS_PRESETS).map(([id, p]) => (
                    <Chip
                        key={id}
                        label={p.label}
                        clickable
                        onClick={() => applyPreset(id)}
                        color={active === id ? 'secondary' : 'default'}
                        variant={active === id ? 'filled' : 'outlined'}
                        sx={{
                            color: active === id ? '#111' : ACCENT,
                            borderColor: ACCENT,
                            bgcolor: active === id ? ACCENT : 'transparent',
                        }}
                    />
                ))}
                <Chip
                    label="Custom"
                    variant={active === 'custom' ? 'filled' : 'outlined'}
                    sx={{
                        color: active === 'custom' ? '#111' : COLOR.textMuted,
                        borderColor: COLOR.textSubtle,
                        bgcolor: active === 'custom' ? COLOR.text : 'transparent',
                    }}
                />
            </Stack>
            <Stack spacing={1}>
                <PlaySlider
                    label={`Joint stiffness · ${Math.round(stiffness * 100)}%`}
                    value={stiffness}
                    onChange={onStiffness}
                />
                <PlaySlider
                    label={`Gravity · ${gravityMs2(gravity).toFixed(1)} m/s²`}
                    value={gravity}
                    onChange={onGravity}
                />
                <PlaySlider
                    label={`Pole weight · ${poleMassKg(poleMass).toFixed(3)} kg`}
                    value={poleMass}
                    onChange={onPoleMass}
                />
            </Stack>
        </Box>
    );
}

function PlaySlider({ label, value, onChange }) {
    return (
        <Box>
            <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', mb: 0.5 }}>{label}</Typography>
            <Slider
                value={value}
                min={0}
                max={1}
                step={0.05}
                onChange={(_, v) => onChange(clamp01(Array.isArray(v) ? v[0] : v, value))}
                sx={{ color: ACCENT, py: 0.5 }}
            />
        </Box>
    );
}
